import axiosInstance from "@/api/axiosInstance";
import {
  CommunityGroup,
  CreateGroupRequest,
  UpdateGroupRequest,
  GroupListResponse,
  MemberListResponse,
  PostListResponse,
  CommentListResponse,
  ModerationLogResponse,
  GroupPost,
  GroupComment,
  GroupMember,
  CreatePostRequest,
  UpdatePostRequest,
  CreateCommentRequest,
  UpdateCommentRequest,
  ReactRequest,
} from "./types";

const BASE_URL = "/api/private";

// ============================================
// Group Management
// ============================================

export const fetchGroups = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  privacy?: string;
  myGroups?: boolean;
}): Promise<GroupListResponse> => {
  const response = await axiosInstance.get(`${BASE_URL}/community-groups`, {
    params,
  });
  return response.data.data;
};

export const fetchGroup = async (uuid: string): Promise<CommunityGroup> => {
  const response = await axiosInstance.get(
    `${BASE_URL}/community-groups/${uuid}`,
  );
  return response.data.data;
};

export const createGroup = async (
  data: CreateGroupRequest,
): Promise<CommunityGroup> => {
  const response = await axiosInstance.post(
    `${BASE_URL}/community-groups`,
    data,
  );
  return response.data.data;
};

export const updateGroup = async (
  uuid: string,
  data: UpdateGroupRequest,
): Promise<CommunityGroup> => {
  const response = await axiosInstance.patch(
    `${BASE_URL}/community-groups/${uuid}`,
    data,
  );
  return response.data.data;
};

export const deleteGroup = async (uuid: string): Promise<void> => {
  await axiosInstance.delete(`${BASE_URL}/community-groups/${uuid}`);
};

// ============================================
// Membership Management
// ============================================

export const joinGroup = async (groupUuid: string): Promise<GroupMember> => {
  const response = await axiosInstance.post(
    `${BASE_URL}/community-groups/${groupUuid}/join`,
  );
  return response.data.data;
};

export const leaveGroup = async (groupUuid: string): Promise<void> => {
  await axiosInstance.post(`${BASE_URL}/community-groups/${groupUuid}/leave`);
};

export const fetchGroupMembers = async (
  groupUuid: string,
  params?: { page?: number; limit?: number },
): Promise<MemberListResponse> => {
  const response = await axiosInstance.get(
    `${BASE_URL}/community-groups/${groupUuid}/members`,
    { params },
  );
  return response.data.data;
};

export const updateMemberRole = async (
  groupUuid: string,
  employeeUuid: string,
  data: { role: "admin" | "moderator" | "member" },
): Promise<GroupMember> => {
  const response = await axiosInstance.patch(
    `${BASE_URL}/community-groups/${groupUuid}/members/${employeeUuid}`,
    data,
  );
  return response.data.data;
};

export const removeMember = async (
  groupUuid: string,
  employeeUuid: string,
): Promise<void> => {
  await axiosInstance.delete(
    `${BASE_URL}/community-groups/${groupUuid}/members/${employeeUuid}`,
  );
};

export const fetchPendingRequests = async (
  groupUuid: string,
  params?: { page?: number; limit?: number },
): Promise<MemberListResponse> => {
  const response = await axiosInstance.get(
    `${BASE_URL}/community-groups/${groupUuid}/join-requests`,
    { params },
  );
  return response.data.data;
};

export const approveJoinRequest = async (
  groupUuid: string,
  employeeUuid: string,
): Promise<GroupMember> => {
  const response = await axiosInstance.post(
    `${BASE_URL}/community-groups/${groupUuid}/join-requests/${employeeUuid}/approve`,
  );
  return response.data.data;
};

export const rejectJoinRequest = async (
  groupUuid: string,
  employeeUuid: string,
): Promise<void> => {
  await axiosInstance.post(
    `${BASE_URL}/community-groups/${groupUuid}/join-requests/${employeeUuid}/reject`,
  );
};

// ============================================
// Post Management
// ============================================

export const fetchGroupPosts = async (
  groupUuid: string,
  params?: { page?: number; limit?: number },
): Promise<PostListResponse> => {
  const response = await axiosInstance.get(
    `${BASE_URL}/community-groups/${groupUuid}/posts`,
    { params },
  );
  return response.data.data;
};

export const fetchFeed = async (params?: {
  page?: number;
  limit?: number;
}): Promise<PostListResponse> => {
  const response = await axiosInstance.get(
    `${BASE_URL}/community-groups/feed`,
    { params },
  );
  return response.data.data;
};

export const fetchPost = async (postUuid: string): Promise<GroupPost> => {
  const response = await axiosInstance.get(
    `${BASE_URL}/community-posts/${postUuid}`,
  );
  return response.data.data;
};

export const createPost = async (
  groupUuid: string,
  data: CreatePostRequest,
): Promise<GroupPost> => {
  const response = await axiosInstance.post(
    `${BASE_URL}/community-groups/${groupUuid}/posts`,
    data,
  );
  return response.data.data;
};

export const updatePost = async (
  postUuid: string,
  data: UpdatePostRequest,
): Promise<GroupPost> => {
  const response = await axiosInstance.patch(
    `${BASE_URL}/community-posts/${postUuid}`,
    data,
  );
  return response.data.data;
};

export const deletePost = async (postUuid: string): Promise<void> => {
  await axiosInstance.delete(`${BASE_URL}/community-posts/${postUuid}`);
};

export const reactToPost = async (
  postUuid: string,
  data: ReactRequest,
): Promise<GroupPost> => {
  const response = await axiosInstance.post(
    `${BASE_URL}/community-posts/${postUuid}/reactions`,
    data,
  );
  return response.data.data;
};

// ============================================
// Comment Management
// ============================================

export const fetchPostComments = async (
  postUuid: string,
  params?: { page?: number; limit?: number },
): Promise<CommentListResponse> => {
  const response = await axiosInstance.get(
    `${BASE_URL}/community-posts/${postUuid}/comments`,
    { params },
  );
  return response.data.data;
};

export const createComment = async (
  postUuid: string,
  data: CreateCommentRequest,
): Promise<GroupComment> => {
  const response = await axiosInstance.post(
    `${BASE_URL}/community-posts/${postUuid}/comments`,
    data,
  );
  return response.data.data;
};

export const updateComment = async (
  commentUuid: string,
  data: UpdateCommentRequest,
): Promise<GroupComment> => {
  const response = await axiosInstance.patch(
    `${BASE_URL}/community-comments/${commentUuid}`,
    data,
  );
  return response.data.data;
};

export const deleteComment = async (commentUuid: string): Promise<void> => {
  await axiosInstance.delete(`${BASE_URL}/community-comments/${commentUuid}`);
};

export const reactToComment = async (
  commentUuid: string,
  data: ReactRequest,
): Promise<GroupComment> => {
  const response = await axiosInstance.post(
    `${BASE_URL}/community-comments/${commentUuid}/reactions`,
    data,
  );
  return response.data.data;
};

// ============================================
// Moderation
// ============================================

export const fetchPendingPosts = async (
  groupUuid: string,
  params?: { page?: number; limit?: number },
): Promise<PostListResponse> => {
  const response = await axiosInstance.get(
    `${BASE_URL}/community-groups/${groupUuid}/moderation/pending-posts`,
    { params },
  );
  return response.data.data;
};

export const approvePost = async (postUuid: string): Promise<GroupPost> => {
  const response = await axiosInstance.post(
    `${BASE_URL}/community-posts/${postUuid}/approve`,
  );
  return response.data.data;
};

export const rejectPost = async (
  postUuid: string,
  reason?: string,
): Promise<void> => {
  await axiosInstance.post(`${BASE_URL}/community-posts/${postUuid}/reject`, {
    reason,
  });
};

export const hideComment = async (
  commentUuid: string,
  reason?: string,
): Promise<void> => {
  await axiosInstance.post(
    `${BASE_URL}/community-comments/${commentUuid}/hide`,
    { reason },
  );
};

export const banMember = async (
  groupUuid: string,
  employeeUuid: string,
  reason: string,
): Promise<GroupMember> => {
  const response = await axiosInstance.post(
    `${BASE_URL}/community-groups/${groupUuid}/members/${employeeUuid}/ban`,
    { reason },
  );
  return response.data.data;
};

export const unbanMember = async (
  groupUuid: string,
  employeeUuid: string,
): Promise<GroupMember> => {
  const response = await axiosInstance.post(
    `${BASE_URL}/community-groups/${groupUuid}/members/${employeeUuid}/unban`,
  );
  return response.data.data;
};

export const fetchBannedMembers = async (
  groupUuid: string,
  params?: { page?: number; limit?: number },
): Promise<MemberListResponse> => {
  const response = await axiosInstance.get(
    `${BASE_URL}/community-groups/${groupUuid}/moderation/banned`,
    { params },
  );
  return response.data.data;
};

export const fetchModerationLog = async (
  groupUuid: string,
  params?: { page?: number; limit?: number },
): Promise<ModerationLogResponse> => {
  const response = await axiosInstance.get(
    `${BASE_URL}/community-groups/${groupUuid}/moderation/log`,
    { params },
  );
  return response.data.data;
};
